//dependencies
import { Chart, Chart as ChartJS, DoughnutController } from "chart.js";
import React, { MutableRefObject, useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import { useParams } from "react-router-dom";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { ButtonStandard, Text } from "@performyard/py-components";

//services
import { useGoalDetails, useRbacObjectPermissions } from "../../../../../../api/hooks";
//hooks
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";

import { Goal } from "../../../../../../api/services";

import { stringToColor } from "../../../../../../utils/getColor";
import { parseGoalPermissionsForUser } from "../../../utils";
//utils
import { getProgressLabels } from "../utils";

//config
import Config from "../../../config";
//styles
import css from "../SingleGoalInfo.module.scss";

//plugins
const endTip = {
	id: "endTip",
	afterDatasetsDraw(chart) {
		const { ctx } = chart;
		const emptyValue = chart.data.datasets[0].data[1];
		const { innerRadius, outerRadius, x, y, endAngle, options } = chart.getDatasetMeta(0).data[0];
		const color = options.borderColor;
		ctx.save();
		ctx.fillStyle = color;
		ctx.lineWidth = 2;
		ctx.beginPath();
		const middleRadius = (innerRadius + outerRadius) / 2;

		const xpos = x + middleRadius * Math.cos(endAngle);
		const ypos = y + middleRadius * Math.sin(endAngle);

		const indicatorRadius = emptyValue > 0 ? 12 : 0;
		ctx.arc(xpos, ypos, indicatorRadius, 0, 2 * Math.PI);
		ctx.fill();

		ctx.restore();
	},
};

const radiusBackground = {
	id: "radiusBackground",

	beforeDatasetsDraw(chart) {
		const { ctx, width, height } = chart;

		const { innerRadius } = chart.getDatasetMeta(chart.data.datasets.length - 1).controller as DoughnutController;
		const { outerRadius } = chart.getDatasetMeta(0).controller as DoughnutController;
		const radiusLength = outerRadius - innerRadius;

		const x = width / 2,
			y = height / 2;

		ctx.beginPath();
		ctx.arc(x, y, outerRadius - radiusLength / 2, 0, 2 * Math.PI);
		ctx.lineWidth = radiusLength;
		ctx.strokeStyle = "#E9EAEC";
		ctx.stroke();
	},
};

const GoalProgressDoughnutChart = () => {
	const { goal_id: goalId } = useParams<{ goal_id: string }>();
	const chartRef = useRef<ChartJS>();

	const { data: goalData } = useGoalDetails({ goalID: goalId || "" });
	const goal = goalData ? goalData.goal : ({} as Goal);
	const { handleQueryChange } = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const { data: permissions } = useRbacObjectPermissions("goal", goalId || "");

	const { userCanEditValue } = parseGoalPermissionsForUser(permissions);
	const isTargetNegative = goal?.target < 0;
	let goalValue = goal?.value || goal?.value === 0 ? goal?.value : 0;
	let goalTarget = goal?.target || goal?.target === 0 ? goal?.target : 0;
	if (isTargetNegative) {
		goalValue = goalValue * -1;
		goalTarget = goalTarget * -1;
	}
	const emptyValue = goalValue < goalTarget ? goalTarget - goalValue : 0;
	let color = goal?.color?.color || "#3E90C1";
	if (color[0] !== "#") {
		color = stringToColor(color);
	}

	const labels = getProgressLabels({ value: goal?.value, units: goal?.units });

	const data = {
		labels: ["Progress", "Progress Left"],
		datasets: [
			{
				data: [goalValue > 0 ? goalValue : 0, emptyValue],
				backgroundColor: [color, "transparent"],
				borderColor: [color === "#FFFFFF" ? "#B7BBC2" : color, "transparent"],
				borderWidth: 1,
			},
		],
	};

	return (
		<div className={css.doughnutGraphCard}>
			<div className={css.doughnutChartContainer}>
				<div className={css.doughnutTextDataContainer}>
					<Text size="heading-3" weight="semi-bold">
						{labels.mainText}
					</Text>
					{labels?.subText && (
						<div className="max-w-[94px] py-line-clamp-2">
							<Text size="paragraph-3" weight="regular">
								{labels.subText}
							</Text>
						</div>
					)}
				</div>
				<div className={css.chartContainer} data-testid="goal-progress-doughnut-chart">
					<Doughnut
						ref={chartRef as MutableRefObject<Chart<"doughnut", any, unknown>>}
						data={data}
						plugins={[endTip, radiusBackground]}
						width={168}
						height={180}
						options={{
							cutout: "80%",
							layout: {
								padding: 8,
							},
							plugins: {
								tooltip: {
									callbacks: {
										label: (yDatapoint) => {
											const trueValue = isTargetNegative
												? Number(yDatapoint?.raw) * -1
												: yDatapoint?.raw;
											const labels = getProgressLabels({
												value: Number(trueValue || 0),
												units: goal?.units,
												decimalPlacesOverride: 2,
											});

											return `${labels.mainText} ${labels.subText}`;
										},
									},
								},
							},
						}}
					/>
				</div>
			</div>
			<div>
				{userCanEditValue && goal?.status === "active" ? (
					<div className="w-fit">
						<ButtonStandard
							iconLeft={faPencil}
							color="neutral"
							variant="outline"
							onClick={() => handleQueryChange({ modal: "updateProgress" })}
						>
							Update Progress
						</ButtonStandard>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default GoalProgressDoughnutChart;
